import { sAutocompleteListSetup, sResetAutocomplete } from '../../shared.min.js';

const passportValidityCalc = {
	autocomplete: null,
	countryNames: [],

	init: () => {
		passportValidityCalc.setupAutocomplete();
		passportValidityCalc.setupDatePickerMinMax();
		passportValidityCalc.setupEventListeners();
	},

	getPassportValidityCalcData: () => {
		const form = document.getElementById('travelForm');
		return JSON.parse(form.getAttribute('data-calc-json'));
	},

	setupAutocomplete: () => {
		const form = document.getElementById('travelForm');
		const passportValidityCalcData = passportValidityCalc.getPassportValidityCalcData();

		// Sort the data
		const sortedCountries = [...passportValidityCalcData.countries].sort((a, b) => {
			const nameA = a.name.toUpperCase();
			const nameB = b.name.toUpperCase();
			if (nameA < nameB) return -1;
			if (nameA > nameB) return 1;
			return 0;
		});

		// Store names for validation
		passportValidityCalc.countryNames = sortedCountries.map(country => country.name);

		// Create a data object in the format sAutocomplete.js expects
		const autocompleteData = {
			countries: sortedCountries,
		};

		// Get the new HTML elements for the autocomplete
		const autocompleteElement = form.querySelector('#countryAutocomplete');
		const autocompleteListElement = form.querySelector('#countryAutocompleteList');
		const selectedItemsContainer = form.querySelector('#countryAutocompleteSelected');

		// Call the setup function from sAutocomplete.js
		passportValidityCalc.autocomplete = sAutocompleteListSetup({
			data: autocompleteData,
			topKey: 'countries',
			autocompleteElement: autocompleteElement,
			autocompleteListElement: autocompleteListElement,
			selectedItemsContainer: selectedItemsContainer,
			selectedItemsContainerTitle: 'Selected Country',
		});
	},

	setupDatePickerMinMax: () => {
		const form = document.getElementById('travelForm');
		const dateOfIssue = form.querySelector('#dateOfIssue');
		const dateOfExpiry = form.querySelector('#dateOfExpiry');
		const dateOfTravel = form.querySelector('#dateOfTravel');
		const dateOfReturn = form.querySelector('#dateOfReturn');
		const today = new Date().toISOString().split('T')[0];

		const minIssueDate = new Date();
		minIssueDate.setFullYear(minIssueDate.getFullYear() - 11);
		const minIssueDateString = minIssueDate.toISOString().split('T')[0];
		dateOfIssue.setAttribute('min', minIssueDateString);
		dateOfIssue.setAttribute('max', today);

		const maxExpiryDate = new Date();
		maxExpiryDate.setFullYear(maxExpiryDate.getFullYear() + 11);
		const maxExpiryDateString = maxExpiryDate.toISOString().split('T')[0];

		dateOfExpiry.setAttribute('min', today);
		dateOfExpiry.setAttribute('max', maxExpiryDateString);

		const maxTravelDate = new Date();
		maxTravelDate.setFullYear(maxTravelDate.getFullYear() + 11);
		const maxTravelDateString = maxTravelDate.toISOString().split('T')[0];

		dateOfTravel.setAttribute('min', today);
		dateOfTravel.setAttribute('max', maxTravelDateString);

		const maxReturnDate = new Date();
		maxReturnDate.setFullYear(maxReturnDate.getFullYear() + 11);
		const maxReturnDateString = maxReturnDate.toISOString().split('T')[0];

		dateOfReturn.setAttribute('min', today);
		dateOfReturn.setAttribute('max', maxReturnDateString);
	},

	setupEventListeners: () => {
		const form = document.getElementById('travelForm');
		const btns = form.querySelectorAll('.btn');
		const fields = form.querySelectorAll('.passport-validator input');
		const backBtns = form.querySelectorAll('.edit-previous');
		const restart = form.querySelector('.restart');

		btns.forEach(btn => {
			btn.addEventListener('click', event => {
				event.preventDefault();
				const currentScreen = passportValidityCalc.ScreenManager.getCurrentScreen();
				const allFieldsFilled = passportValidityCalc.validateScreenInputs(currentScreen);
				if (allFieldsFilled) {
					passportValidityCalc.ScreenManager.progressScreen(currentScreen);
				}
			});
		});

		fields.forEach(field => {
			field.addEventListener('focusout', () => {
				if (field.type === 'date') {
					passportValidityCalc.validateDateInput(field);
				} else if (field.id === 'countryAutocomplete') {
					// Use a slight delay to allow the autocomplete 'click'
					// to set the value before validation runs.
					// This was the easiest way to handle it without
					// modifying the autocomplete component itself.
					setTimeout(() => {
						passportValidityCalc.validateCountryInput(field);
					}, 200);
				}
			});
		});

		backBtns.forEach(backBtn => {
			backBtn.addEventListener('click', event => {
				event.preventDefault();
				const currentScreen = passportValidityCalc.ScreenManager.getCurrentScreen();
				passportValidityCalc.ScreenManager.regressScreen(currentScreen);
			});
		});

		restart.addEventListener('click', event => {
			event.preventDefault();
			const currentScreen = passportValidityCalc.ScreenManager.getCurrentScreen();
			passportValidityCalc.ScreenManager.resetForm(currentScreen);
		});
	},

	validateDateInput: field => {
		if (field.value === '') {
			field.closest('.field-group').classList.add('field-group--error');
			field.setAttribute('aria-invalid', 'true');
			return false;
		} else {
			field.closest('.field-group').classList.remove('field-group--error');
			field.setAttribute('aria-invalid', 'false');
			return true;
		}
	},

	validateCountryInput: field => {
		// Validate that the input value is a non-empty string
		// and that it exists in our original list of country names.
		const isValid = field.value !== '' && passportValidityCalc.countryNames.includes(field.value);
		const fieldGroup = field.closest('.field-group');

		if (!isValid) {
			fieldGroup.classList.add('field-group--error'); // Add error class to parent
			field.setAttribute('aria-invalid', 'true');
			return false;
		} else {
			fieldGroup.classList.remove('field-group--error'); // Remove error class from parent
			field.setAttribute('aria-invalid', 'false');
			return true;
		}
	},

	validateScreenInputs: screen => {
		let allFieldsFilled = true;
		const fields = screen.querySelectorAll('input');

		fields.forEach(field => {
			if (field.type === 'date') {
				if (!passportValidityCalc.validateDateInput(field)) {
					allFieldsFilled = false;
				}
			} else if (field.id === 'countryAutocomplete') {
				if (!passportValidityCalc.validateCountryInput(field)) {
					allFieldsFilled = false;
				}
			}
		});

		return allFieldsFilled;
	},

	ScreenManager: {
		getCurrentScreen: () => {
			const form = document.getElementById('travelForm');
			const screens = form.querySelectorAll('.screen');
			let currentScreen = null;
			screens.forEach(screen => {
				if (!screen.classList.contains('display-none')) {
					currentScreen = screen;
				}
			});
			return currentScreen;
		},

		progressScreen: currentScreen => {
			const form = document.getElementById('travelForm');
			const bar = form.querySelector('.passport-validator__progress');
			const progress = form.querySelector('.passport-validator__progress--count');
			const progressIndicator = form.querySelector('.passport-validator__progress--indicator');
			const sectionTitles = form.querySelectorAll('.passport-validator__progress--title');
			const sectionTitle1 = sectionTitles[0];
			const sectionTitle2 = sectionTitles[1];
			const sectionTitle3 = sectionTitles[2];

			sectionTitles.forEach(title => {
				title.classList.add('display-none');
			});

			progress.textContent = '1';

			if (currentScreen.classList.contains('passport-validator__screen-1')) {
				currentScreen.classList.add('display-none');
				form.querySelector('.passport-validator__screen-2').classList.remove('display-none');
				progress.textContent = '2';
				progressIndicator.classList.remove('screen-1');
				progressIndicator.classList.add('screen-2');
				sectionTitle1.classList.add('display-none');
				sectionTitle2.classList.remove('display-none');
			} else if (currentScreen.classList.contains('passport-validator__screen-2')) {
				passportValidityCalc.checkValidity();
				currentScreen.classList.add('display-none');
				form.querySelector('.passport-validator__screen-3').classList.remove('display-none');
				progress.textContent = '3';
				progressIndicator.classList.remove('screen-2');
				progressIndicator.classList.add('screen-3');
				sectionTitle2.classList.add('display-none');
				sectionTitle3.classList.remove('display-none');
				bar.scrollIntoView({ behavior: 'smooth' });
			}
		},

		regressScreen: currentScreen => {
			const form = document.getElementById('travelForm');
			const progress = form.querySelector('.passport-validator__progress--count');
			const progressIndicator = form.querySelector('.passport-validator__progress--indicator');
			progress.textContent = '1';
			const sectionTitles = form.querySelectorAll('.passport-validator__progress--title');
			const sectionTitle1 = sectionTitles[0];
			const sectionTitle2 = sectionTitles[1];
			const sectionTitle3 = sectionTitles[2];

			sectionTitles.forEach(title => {
				title.classList.add('display-none');
			});

			if (currentScreen.classList.contains('passport-validator__screen-2')) {
				currentScreen.classList.add('display-none');
				form.querySelector('.passport-validator__screen-1').classList.remove('display-none');
				progress.textContent = '1';
				progressIndicator.classList.add('screen-1');
				progressIndicator.classList.remove('screen-2');
				sectionTitle2.classList.add('display-none');
				sectionTitle1.classList.remove('display-none');
			} else if (currentScreen.classList.contains('passport-validator__screen-3')) {
				currentScreen.classList.add('display-none');
				form.querySelector('.passport-validator__screen-2').classList.remove('display-none');
				progress.textContent = '2';
				progressIndicator.classList.add('screen-2');
				progressIndicator.classList.remove('screen-3');
				sectionTitle3.classList.add('display-none');
				sectionTitle2.classList.remove('display-none');
			}
		},

		resetForm: currentScreen => {
			const form = document.getElementById('travelForm');
			const progress = form.querySelector('.passport-validator__progress--count');
			const progressIndicator = form.querySelector('.passport-validator__progress--indicator');
			const dateOfIssue = form.querySelector('#dateOfIssue');
			const dateOfExpiry = form.querySelector('#dateOfExpiry');
			const dateOfTravel = form.querySelector('#dateOfTravel');
			const dateOfReturn = form.querySelector('#dateOfReturn');
			const firstScreen = form.querySelector('.passport-validator__screen-1');
			const destination = form.querySelector('.destination');

			dateOfIssue.value = '';
			dateOfExpiry.value = '';
			dateOfTravel.value = '';
			dateOfReturn.value = '';
			destination.textContent = '';

			// Use the sResetAutocomplete function to clear the component
			if (passportValidityCalc.autocomplete) {
				sResetAutocomplete(
					passportValidityCalc.autocomplete.selectedItemsArray,
					passportValidityCalc.autocomplete.selectedItemsContainer,
					passportValidityCalc.autocomplete.autocompleteElement,
					passportValidityCalc.autocomplete.autocompleteListElement
				);
			}

			progress.textContent = '1';
			progressIndicator.classList.add('screen-1');
			progressIndicator.classList.remove('screen-2', 'screen-3');

			currentScreen.classList.add('display-none');
			firstScreen.classList.remove('display-none');
		},
	},

	checkValidity: () => {
		const form = document.getElementById('travelForm');
		const dateOfIssue = new Date(form.querySelector('#dateOfIssue').value);
		const dateOfExpiry = new Date(form.querySelector('#dateOfExpiry').value);
		const dateOfTravel = new Date(form.querySelector('#dateOfTravel').value);
		const dateOfReturn = new Date(form.querySelector('#dateOfReturn').value);
		const selectedCountry = form.querySelector('#countryAutocomplete').value;
		const validResult = form.querySelector('.passport-validator__result--valid');
		const validResultStay = form.querySelector('.valid-reason--stay');
		const validResultRecommended = form.querySelector('.valid-reason--recommended');
		const invalidResult = form.querySelector('.passport-validator__result--invalid');
		const invalidResultYears = form.querySelector('.invalid-reason--years');
		const invalidResultPost = form.querySelector('.invalid-reason--post');
		const invalidResultMid = form.querySelector('.invalid-reason--mid');
		const invalidResultYearsAmount = form.querySelector('.invalid-years-amount');
		const invalidResultPostAmount = form.querySelector('.invalid-post-amount');
		const invalidResultPostAmountAfterArrival = form.querySelector('.invalid-post-amount-after-arrival');
		const invalidResultPostAmountAfterReturn = form.querySelector('.invalid-post-amount-after-return');
		const destination = form.querySelector('.destination');

		const passportValidityCalcData = passportValidityCalc.getPassportValidityCalcData();
		const country = passportValidityCalcData.countries.find(c => c.name === selectedCountry);

		let expiryCutoffDate = '';
		let expiryCutoffPoint = '';
		let result = true;

		// Hide all results sections
		validResult.classList.add('display-none');
		validResultStay.classList.add('display-none');
		validResultRecommended.classList.add('display-none');
		invalidResult.classList.add('display-none');
		invalidResultPost.classList.add('display-none');
		invalidResultPostAmount.textContent = '';
		invalidResultYears.classList.add('display-none');
		invalidResultYearsAmount.textContent = '';
		invalidResultMid.classList.add('display-none');

		// Check date of issue restriction
		if (country.dateOfIssueRestrictionYears !== null) {
			const issueCutoffDate = new Date(dateOfTravel);
			issueCutoffDate.setFullYear(issueCutoffDate.getFullYear() - country.dateOfIssueRestrictionYears);
			if (dateOfIssue < issueCutoffDate) {
				result = false;
				invalidResult.classList.remove('display-none');
				invalidResultYears.classList.remove('display-none');
				invalidResultYearsAmount.textContent = country.dateOfIssueRestrictionYears;
			}
		}

		// Check expiry date restriction
		if (country.expiryDateRestriction !== null) {
			if (country.expiryfrom === 'entry') {
				expiryCutoffDate = new Date(dateOfTravel);
				expiryCutoffPoint = invalidResultPostAmountAfterArrival.textContent;
			} else {
				expiryCutoffDate = new Date(dateOfReturn);
				expiryCutoffPoint = invalidResultPostAmountAfterReturn.textContent;
			}
			if (country.expiryUnit === 'months') {
				expiryCutoffDate.setMonth(expiryCutoffDate.getMonth() + country.expiryDateRestriction);
			} else {
				// set the expiry date plus the days
				expiryCutoffDate.setDate(expiryCutoffDate.getDate() + country.expiryDateRestriction);
			}
			if (dateOfExpiry < expiryCutoffDate) {
				result = false;
				invalidResult.classList.remove('display-none');
				invalidResultPost.classList.remove('display-none');
				invalidResultPostAmount.innerHTML = `<span class="bold">${country.expiryDateRestriction} ${country.expiryUnit}</span> ${expiryCutoffPoint}`;
			}
		}

		if (dateOfExpiry < dateOfReturn || dateOfIssue > dateOfTravel) {
			result = false;
			invalidResult.classList.remove('display-none');
			invalidResultMid.classList.remove('display-none');
		}

		if (result === true) {
			destination.textContent = ` to ${selectedCountry}`;
			validResult.classList.remove('display-none');

			if (country.expiryfrom === 'stay') {
				validResultStay.classList.remove('display-none');
			}

			if (country.recommendations != null) {
				validResultRecommended.textContent = country.recommendations;
				validResultRecommended.classList.remove('display-none');
			}
		}
	},
};

document.addEventListener('DOMContentLoaded', function () {
	passportValidityCalc.init();
});
